import PropTypes from 'prop-types'
import { cn } from '../../lib/cn'

/**
 * Toggle (Switch) component for boolean settings
 *
 * @component
 * @example
 * <Toggle
 *   label="Enable notifications"
 *   checked={enabled}
 *   onChange={(checked) => setEnabled(checked)}
 * />
 */
export default function Toggle({
  label,
  checked = false,
  onChange,
  disabled = false,
  className = '',
  ...props
}) {
  const handleClick = () => {
    if (!disabled) {
      onChange(!checked)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      handleClick()
    }
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        checked ? 'bg-primary-600' : 'bg-gray-200',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer',
        className
      )}
      {...props}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1'
        )}
      />
    </button>
  )
}

Toggle.propTypes = {
  /** Label for accessibility */
  label: PropTypes.string,
  /** Checked state */
  checked: PropTypes.bool,
  /** Change handler receives boolean */
  onChange: PropTypes.func.isRequired,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string
}
